import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "block-content" }
const _hoisted_2 = { class: "block-content" }

import ClientList from "@/components/main/search-panel/ClientList.vue";
import ProjectSearchPanel from "@/components/main/search-panel/ProjectSearchPanel.vue";
import {computed, ref, watch, onMounted, onUnmounted} from "vue";
import { useBreakpoints } from "@/composables/useBreakpoints";

const collapsedTopHeight = 32
const collapsedBottomHeight = 32
const minTopHeight = 102
const minBottomHeight = 200
const storageKey = 'search-block-positions_v1.2'


export default /*@__PURE__*/_defineComponent({
  __name: 'SearchPanel',
  emits: ['collapse-change'],
  setup(__props, { expose: __expose, emit: __emit }) {

const emit = __emit

const containerRef = ref<HTMLElement | null>(null)
const containerHeight = ref(0)

// Храним высоты в процентах
const topBlockHeightPercent = ref(0.5)
const bottomBlockHeightPercent = ref(0.5)

// Сохраняем размеры для возврата при разворачивании
const savedTopPercent = ref(0.5)
const savedBottomPercent = ref(0.5)

// Флаги свернутости
const collapsedTop = ref(false)
const collapsedBottom = ref(false)

// Вычисляемые высоты в пикселях
const topBlockHeightPx = computed(() => topBlockHeightPercent.value * containerHeight.value)
const bottomBlockHeightPx = computed(() => bottomBlockHeightPercent.value * containerHeight.value)

// Проценты для collapsed состояний
const collapsedTopHeightPercent = computed(() => collapsedTopHeight / containerHeight.value)
const collapsedBottomHeightPercent = computed(() => collapsedBottomHeight / containerHeight.value)

// Минимальные высоты в процентах
const minTopHeightPercent = computed(() => minTopHeight / containerHeight.value)
const minBottomHeightPercent = computed(() => minBottomHeight / containerHeight.value)

const isVerticalResizing = ref(false)
const startY = ref(0)
const startTopHeightPercent = ref(0)
const startBottomHeightPercent = ref(0)
const initialCollapsedTop = ref(false)
const initialCollapsedBottom = ref(false)

const visualVerticalResizerTop = ref(0)
const visualVerticalResizerLeft = ref(0)
const visualVerticalResizerWidth = ref(0)

const bothExpanded = computed(() => !collapsedTop.value && !collapsedBottom.value)

const { isMobile, isTablet } = useBreakpoints()
const isCompactMode = computed(() => isMobile.value || isTablet.value)

const useResizeObserver = (element: HTMLElement, callback: () => void) => {
	let frame = 0
	const observer = new ResizeObserver((entries) => {
		cancelAnimationFrame(frame)
		frame = requestAnimationFrame(() => {
			if (Array.isArray(entries) && entries.length) {
				callback()
			}
		})
	})
	observer.observe(element)
	return observer
}

const loadStateFromStorage = () => {
	try {
		const saved = localStorage.getItem(storageKey)
		if (saved) {
			return JSON.parse(saved)
		}
	} catch (error) {
		console.warn('Не удалось загрузить состояние из localStorage:', error)
	}
	return null
}

const saveStateToStorage = () => {
	try {
		const stateToSave = {
			topHeightPercent: topBlockHeightPercent.value,
			bottomHeightPercent: bottomBlockHeightPercent.value,
			savedTopPercent: savedTopPercent.value,
			savedBottomPercent: savedBottomPercent.value,
			collapsedTop: collapsedTop.value,
			collapsedBottom: collapsedBottom.value
		}
		localStorage.setItem(storageKey, JSON.stringify(stateToSave))
	} catch (error) {
		console.warn('Не удалось сохранить состояние в localStorage:', error)
	}
}

const initializeHeights = () => {
	if (!containerRef.value) return
	updateContainerHeight()

	const savedState = loadStateFromStorage()
	if (isMobile.value) {
		resetToNormal()
	} else if (savedState) {
		topBlockHeightPercent.value = savedState.topHeightPercent
		bottomBlockHeightPercent.value = savedState.bottomHeightPercent
		savedTopPercent.value = savedState.savedTopPercent || 0.5
		savedBottomPercent.value = savedState.savedBottomPercent || 0.5
		collapsedTop.value = savedState.collapsedTop || false
		collapsedBottom.value = savedState.collapsedBottom || false

		// При инициализации применяем collapsed состояния
		if (collapsedTop.value) {
			topBlockHeightPercent.value = collapsedTopHeightPercent.value
			bottomBlockHeightPercent.value = 1 - collapsedTopHeightPercent.value
		} else if (collapsedBottom.value) {
			bottomBlockHeightPercent.value = collapsedBottomHeightPercent.value
			topBlockHeightPercent.value = 1 - collapsedBottomHeightPercent.value
		}
	} else {
		resetToNormal()
	}

	// В compact mode всегда один свёрнут, другой развёрнут
	if (isTablet.value && !collapsedTop.value && !collapsedBottom.value) {
		savedTopPercent.value = topBlockHeightPercent.value
		savedBottomPercent.value = bottomBlockHeightPercent.value
		collapsedBottom.value = true
		collapsedTop.value = false
		topBlockHeightPercent.value = 1 - collapsedBottomHeightPercent.value
		bottomBlockHeightPercent.value = collapsedBottomHeightPercent.value
	}
}

const updateContainerHeight = () => {
	if (containerRef.value) {
		containerHeight.value = containerRef.value.clientHeight
	}
}

const resetToNormal = () => {
	topBlockHeightPercent.value = 0.5
	bottomBlockHeightPercent.value = 0.5
	savedTopPercent.value = 0.5
	savedBottomPercent.value = 0.5
	collapsedTop.value = false
	collapsedBottom.value = false
}

const toggleMobileBlock = (block: 'top' | 'bottom') => {
	const clickedBlockIsFullscreen =
		(block === 'top' && !collapsedTop.value && collapsedBottom.value) ||
		(block === 'bottom' && collapsedTop.value && !collapsedBottom.value)

	if (clickedBlockIsFullscreen) {
		resetToNormal()
		return
	}

	const showTop = block === 'top'
	collapsedTop.value = !showTop
	collapsedBottom.value = showTop
	topBlockHeightPercent.value = showTop
		? 1 - collapsedBottomHeightPercent.value
		: collapsedTopHeightPercent.value
	bottomBlockHeightPercent.value = 1 - topBlockHeightPercent.value
}

const toggleBlock = (block: 'top' | 'bottom') => {
	if (isMobile.value) {
		toggleMobileBlock(block)
		emitCollapseChange()
		return
	}

	if (block === 'top') {
		if (collapsedTop.value) {
			// Верхний был свернут - разворачиваем верхний и сворачиваем нижний
			// Запоминаем текущие размеры перед изменением
			savedTopPercent.value = topBlockHeightPercent.value
			savedBottomPercent.value = bottomBlockHeightPercent.value

			collapsedTop.value = false
			collapsedBottom.value = true

			// Применяем новые размеры
			topBlockHeightPercent.value = 1 - collapsedBottomHeightPercent.value
			bottomBlockHeightPercent.value = collapsedBottomHeightPercent.value
		} else {
			if (collapsedBottom.value) {
				if (isCompactMode.value) {
					// COMPACT: swap — свернуть верхний, развернуть нижний
					collapsedTop.value = true
					collapsedBottom.value = false
					topBlockHeightPercent.value = collapsedTopHeightPercent.value
					bottomBlockHeightPercent.value = 1 - collapsedTopHeightPercent.value
				} else {
					// Нижний свернут, верхний развернут - это состояние после первого клика
					// Возвращаемся к сохраненным размерам (до первого клика)
					collapsedTop.value = false
					collapsedBottom.value = false

					// Восстанавливаем размеры из savedTopPercent/savedBottomPercent
					topBlockHeightPercent.value = savedTopPercent.value
					bottomBlockHeightPercent.value = savedBottomPercent.value
				}
			} else {
				// Оба развернуты - это исходное состояние
				// Запоминаем текущие размеры и сворачиваем нижний
				savedTopPercent.value = topBlockHeightPercent.value
				savedBottomPercent.value = bottomBlockHeightPercent.value

				collapsedTop.value = false
				collapsedBottom.value = true

				// Применяем новые размеры
				topBlockHeightPercent.value = 1 - collapsedBottomHeightPercent.value
				bottomBlockHeightPercent.value = collapsedBottomHeightPercent.value
			}
		}
	} else if (block === 'bottom') {
		if (collapsedBottom.value) {
			// Нижний был свернут - разворачиваем нижний и сворачиваем верхний
			// Запоминаем текущие размеры перед изменением
			savedTopPercent.value = topBlockHeightPercent.value
			savedBottomPercent.value = bottomBlockHeightPercent.value

			collapsedTop.value = true
			collapsedBottom.value = false

			// Применяем новые размеры
			bottomBlockHeightPercent.value = 1 - collapsedTopHeightPercent.value
			topBlockHeightPercent.value = collapsedTopHeightPercent.value
		} else {
			if (collapsedTop.value) {
				if (isCompactMode.value) {
					// COMPACT: swap — свернуть нижний, развернуть верхний
					collapsedBottom.value = true
					collapsedTop.value = false
					bottomBlockHeightPercent.value = collapsedBottomHeightPercent.value
					topBlockHeightPercent.value = 1 - collapsedBottomHeightPercent.value
				} else {
					// Верхний свернут, нижний развернут - это состояние после первого клика
					// Возвращаемся к сохраненным размерам (до первого клика)
					collapsedTop.value = false
					collapsedBottom.value = false

					// Восстанавливаем размеры из savedTopPercent/savedBottomPercent
					topBlockHeightPercent.value = savedTopPercent.value
					bottomBlockHeightPercent.value = savedBottomPercent.value
				}
			} else {
				// Оба развернуты - это исходное состояние
				// Запоминаем текущие размеры и сворачиваем верхний
				savedTopPercent.value = topBlockHeightPercent.value
				savedBottomPercent.value = bottomBlockHeightPercent.value

				collapsedTop.value = true
				collapsedBottom.value = false

				// Применяем новые размеры
				bottomBlockHeightPercent.value = 1 - collapsedTopHeightPercent.value
				topBlockHeightPercent.value = collapsedTopHeightPercent.value
			}
		}
	}

	saveStateToStorage()
	emitCollapseChange()
}

const emitCollapseChange = () => {
	emit('collapse-change', {
		topCollapsed: collapsedTop.value,
		bottomCollapsed: collapsedBottom.value
	})
}

const startVerticalResize = (event: MouseEvent) => {
	if (isCompactMode.value) return
	event.preventDefault()

	if (!containerRef.value) return

	const containerRect = containerRef.value.getBoundingClientRect()
	visualVerticalResizerLeft.value = containerRect.left
	visualVerticalResizerWidth.value = containerRect.width

	isVerticalResizing.value = true
	startY.value = event.clientY
	startTopHeightPercent.value = topBlockHeightPercent.value
	startBottomHeightPercent.value = bottomBlockHeightPercent.value

	initialCollapsedTop.value = collapsedTop.value
	initialCollapsedBottom.value = collapsedBottom.value

	visualVerticalResizerTop.value = containerRect.top + topBlockHeightPx.value

	document.addEventListener('mousemove', handleVerticalResizeMove)
	document.addEventListener('mouseup', stopVerticalResize)
	document.body.style.userSelect = 'none'
	document.body.style.cursor = 'row-resize'
}

let resizeRafId: number | null = null
let lastMoveEvent: MouseEvent | null = null

const cancelResizeRaf = () => {
	if (resizeRafId !== null) {
		cancelAnimationFrame(resizeRafId)
		resizeRafId = null
	}
}

const applyVerticalResize = (event: MouseEvent) => {
	if (!isVerticalResizing.value || !containerRef.value) {
		return
	}

	const deltaY = event.clientY - startY.value
	const deltaPercent = deltaY / containerHeight.value

	let newTopHeightPercent = startTopHeightPercent.value + deltaPercent
	let newBottomHeightPercent = startBottomHeightPercent.value - deltaPercent

	// Определяем текущее состояние
	const currentTopCollapsed = collapsedTop.value
	const currentBottomCollapsed = collapsedBottom.value

	// Логика для ресайзера
	if (!currentTopCollapsed && !currentBottomCollapsed) {
		if (newTopHeightPercent < minTopHeightPercent.value) {
			collapsedTop.value = true
			newTopHeightPercent = collapsedTopHeightPercent.value
			newBottomHeightPercent = 1 - collapsedTopHeightPercent.value

			// При схватывании запоминаем размеры до схватывания
			if (!initialCollapsedTop.value) {
				savedTopPercent.value = startTopHeightPercent.value
				savedBottomPercent.value = startBottomHeightPercent.value
			}
		} else if (newBottomHeightPercent < minBottomHeightPercent.value) {
			collapsedBottom.value = true
			newBottomHeightPercent = collapsedBottomHeightPercent.value
			newTopHeightPercent = 1 - collapsedBottomHeightPercent.value

			// При схватывании запоминаем размеры до схватывания
			if (!initialCollapsedBottom.value) {
				savedTopPercent.value = startTopHeightPercent.value
				savedBottomPercent.value = startBottomHeightPercent.value
			}
		}
	} else if (currentTopCollapsed && !currentBottomCollapsed) {
		if (newTopHeightPercent >= minTopHeightPercent.value) {
			collapsedTop.value = false
			collapsedBottom.value = false

			if (newBottomHeightPercent < minBottomHeightPercent.value) {
				collapsedBottom.value = true
				newBottomHeightPercent = collapsedBottomHeightPercent.value
				newTopHeightPercent = 1 - collapsedBottomHeightPercent.value
			}
		} else if (newBottomHeightPercent < minBottomHeightPercent.value) {
			collapsedTop.value = false
			collapsedBottom.value = true
			newBottomHeightPercent = collapsedBottomHeightPercent.value
			newTopHeightPercent = 1 - collapsedBottomHeightPercent.value
		}
	} else if (!currentTopCollapsed && currentBottomCollapsed) {
		if (newBottomHeightPercent >= minBottomHeightPercent.value) {
			collapsedTop.value = false
			collapsedBottom.value = false

			if (newTopHeightPercent < minTopHeightPercent.value) {
				collapsedTop.value = true
				newTopHeightPercent = collapsedTopHeightPercent.value
				newBottomHeightPercent = 1 - collapsedTopHeightPercent.value
			}
		} else if (newTopHeightPercent < minTopHeightPercent.value) {
			collapsedTop.value = true
			collapsedBottom.value = false
			newTopHeightPercent = collapsedTopHeightPercent.value
			newBottomHeightPercent = 1 - collapsedTopHeightPercent.value
		}
	}

	// Проверяем общие ограничения для свернутых блоков
	if (collapsedTop.value && newTopHeightPercent > collapsedTopHeightPercent.value) {
		newTopHeightPercent = collapsedTopHeightPercent.value
		newBottomHeightPercent = 1 - collapsedTopHeightPercent.value
	}

	if (collapsedBottom.value && newBottomHeightPercent > collapsedBottomHeightPercent.value) {
		newBottomHeightPercent = collapsedBottomHeightPercent.value
		newTopHeightPercent = 1 - collapsedBottomHeightPercent.value
	}

	// Гарантируем минимальные высоты для свернутых блоков
	if (newTopHeightPercent < collapsedTopHeightPercent.value) {
		newTopHeightPercent = collapsedTopHeightPercent.value
		newBottomHeightPercent = 1 - collapsedTopHeightPercent.value
	}

	if (newBottomHeightPercent < collapsedBottomHeightPercent.value) {
		newBottomHeightPercent = collapsedBottomHeightPercent.value
		newTopHeightPercent = 1 - collapsedBottomHeightPercent.value
	}

	// Корректируем сумму процентов
	const totalPercent = newTopHeightPercent + newBottomHeightPercent
	if (Math.abs(totalPercent - 1) > 0.001) {
		newBottomHeightPercent = 1 - newTopHeightPercent
	}

	// Обновляем позицию визуального ресайзера
	const containerRect = containerRef.value.getBoundingClientRect()
	visualVerticalResizerTop.value = containerRect.top + (newTopHeightPercent * containerHeight.value)

	// Обновляем проценты
	topBlockHeightPercent.value = newTopHeightPercent
	bottomBlockHeightPercent.value = newBottomHeightPercent
}

const handleVerticalResizeMove = (event: MouseEvent) => {
	lastMoveEvent = event
	if (resizeRafId !== null) {
		return
	}
	resizeRafId = requestAnimationFrame(() => {
		resizeRafId = null
		if (lastMoveEvent) {
			applyVerticalResize(lastMoveEvent)
		}
	})
}

const stopVerticalResize = () => {
	if (!isVerticalResizing.value) {
		return
	}

	// Флашим последний отложенный кадр, чтобы итоговая позиция была точной
	if (resizeRafId !== null) {
		cancelResizeRaf()
		if (lastMoveEvent) {
			applyVerticalResize(lastMoveEvent)
		}
	}

	// Финальная корректировка после ресайза
	if (collapsedTop.value && topBlockHeightPercent.value > collapsedTopHeightPercent.value) {
		topBlockHeightPercent.value = collapsedTopHeightPercent.value
		bottomBlockHeightPercent.value = 1 - collapsedTopHeightPercent.value
	}

	if (collapsedBottom.value && bottomBlockHeightPercent.value > collapsedBottomHeightPercent.value) {
		bottomBlockHeightPercent.value = collapsedBottomHeightPercent.value
		topBlockHeightPercent.value = 1 - collapsedBottomHeightPercent.value
	}

	if (!collapsedTop.value && topBlockHeightPercent.value < minTopHeightPercent.value) {
		topBlockHeightPercent.value = minTopHeightPercent.value
		bottomBlockHeightPercent.value = 1 - minTopHeightPercent.value
	}

	if (!collapsedBottom.value && bottomBlockHeightPercent.value < minBottomHeightPercent.value) {
		bottomBlockHeightPercent.value = minBottomHeightPercent.value
		topBlockHeightPercent.value = 1 - minBottomHeightPercent.value
	}

	// Сохраняем размеры после ресайза как сохраненные, если оба развернуты
	if (bothExpanded.value) {
		savedTopPercent.value = topBlockHeightPercent.value
		savedBottomPercent.value = bottomBlockHeightPercent.value
	}

	saveStateToStorage()
	emitCollapseChange()

	isVerticalResizing.value = false
	document.removeEventListener('mousemove', handleVerticalResizeMove)
	document.removeEventListener('mouseup', stopVerticalResize)
	document.body.style.userSelect = ''
	document.body.style.cursor = ''
}

const setCollapsedState = (top: boolean, bottom: boolean) => {
	collapsedTop.value = top
	collapsedBottom.value = bottom

	if (top) {
		topBlockHeightPercent.value = collapsedTopHeightPercent.value
		bottomBlockHeightPercent.value = 1 - collapsedTopHeightPercent.value
	} else if (bottom) {
		bottomBlockHeightPercent.value = collapsedBottomHeightPercent.value
		topBlockHeightPercent.value = 1 - collapsedBottomHeightPercent.value
	}

	saveStateToStorage()
	emitCollapseChange()
}

// Обновляем высоту контейнера при изменении масштаба
const handleResizeWindow = () => {
	updateContainerHeight()
}

// При переходе в compact mode — принудительно один свёрнут
watch(isTablet, (tablet) => {
	if (tablet && !collapsedTop.value && !collapsedBottom.value) {
		savedTopPercent.value = topBlockHeightPercent.value
		savedBottomPercent.value = bottomBlockHeightPercent.value
		collapsedBottom.value = true
		collapsedTop.value = false
		topBlockHeightPercent.value = 1 - collapsedBottomHeightPercent.value
		bottomBlockHeightPercent.value = collapsedBottomHeightPercent.value
		saveStateToStorage()
		emitCollapseChange()
	}
})

watch(isMobile, (mobile) => {
	if (!mobile) return
	resetToNormal()
	emitCollapseChange()
})

let resizeObserver: ResizeObserver | null = null

onMounted(() => {
	initializeHeights()
	window.addEventListener('resize', handleResizeWindow)

	if (containerRef.value) {
		resizeObserver = useResizeObserver(containerRef.value, () => {
			updateContainerHeight()
		})
	}
})

onUnmounted(() => {
	cancelResizeRaf()
	document.removeEventListener('mousemove', handleVerticalResizeMove)
	document.removeEventListener('mouseup', stopVerticalResize)
	window.removeEventListener('resize', handleResizeWindow)

	if (resizeObserver) {
		resizeObserver.disconnect()
	}
})

__expose({
	resetToNormal,
	setCollapsedState,
	toggleBlock
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "vertical-container",
    ref_key: "containerRef",
    ref: containerRef
  }, [
    _createElementVNode("div", {
      class: _normalizeClass(["vertical-block top-block", { 'collapsed': collapsedTop.value }]),
      style: _normalizeStyle({
				height: topBlockHeightPx.value + 'px',
				minHeight: collapsedTop.value ? collapsedTopHeight + 'px' : minTopHeight + 'px'
			})
    }, [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(ClientList, {
          "is-collapsed": collapsedTop.value,
          "is-second-collapsed": collapsedBottom.value,
          onOnToggle: _cache[0] || (_cache[0] = ($event: any) => (toggleBlock('top')))
        }, null, 8, ["is-collapsed", "is-second-collapsed"])
      ])
    ], 6),
    _createElementVNode("div", {
      class: _normalizeClass(["vertical-resizer", { 'vertical-resizer--disabled': isCompactMode.value }]),
      onMousedown: _cache[1] || (_cache[1] = ($event: any) => (startVerticalResize($event)))
    }, null, 34),
    _createElementVNode("div", {
      class: _normalizeClass(["vertical-block bottom-block", { 'collapsed': collapsedBottom.value }]),
      style: _normalizeStyle({
                height: bottomBlockHeightPx.value + 'px',
                minHeight: collapsedBottom.value ? collapsedBottomHeight + 'px' : minBottomHeight + 'px'
			})
    }, [
      _createElementVNode("div", _hoisted_2, [
        _createVNode(ProjectSearchPanel, {
          "is-collapsed": collapsedBottom.value,
          "is-second-collapsed": collapsedTop.value,
          onOnToggle: _cache[2] || (_cache[2] = ($event: any) => (toggleBlock('bottom')))
        }, null, 8, ["is-collapsed", "is-second-collapsed"])
      ])
    ], 6),
    (isVerticalResizing.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "visual-resizer vertical",
          style: _normalizeStyle({
				top: visualVerticalResizerTop.value + 'px',
				left: visualVerticalResizerLeft.value + 'px',
				width: visualVerticalResizerWidth.value + 'px'
			})
        }, null, 4))
      : _createCommentVNode("", true)
  ], 512))
}
}

})